import { Link } from "react-router-dom";
import Navbar from "../../components/auth/Navabar";

/** CEO message video — MP4 URL or YouTube/Vimeo link. */
const SOLUTION_VIDEO = {
  src: "",
  poster: "",
  title: "A message from our CEO",
  speaker: "CEO, AxionNest",
  description:
    "Hear why we built AxionNest and how we connect research, learning and industry in one place.",
};

const getEmbedUrl = (url) => {
  if (!url) return null;
  const youtubeMatch = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  if (youtubeMatch) {
    return `https://www.youtube.com/embed/${youtubeMatch[1]}?rel=0`;
  }
  const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }
  if (/\.(mp4|webm|ogg)(\?|$)/i.test(url) || url.startsWith("blob:")) {
    return null;
  }
  if (url.includes("youtube.com/embed") || url.includes("player.vimeo.com")) {
    return url;
  }
  return null;
};

const HERO_BACKGROUND =
  "radial-gradient(circle at 12% 18%, rgba(79,99,246,.16), transparent 28%), radial-gradient(circle at 88% 15%, rgba(34,184,132,.14), transparent 28%), linear-gradient(180deg, #fff, #f8fbff)";

const CeoVideoSection = ({ src, poster, title, speaker, description }) => {
  const embedUrl = getEmbedUrl(src);

  return (
    <section
      className="relative w-full h-screen min-h-[560px] overflow-hidden bg-[#F9F7F4]"
      style={{ background: HERO_BACKGROUND }}
    >
      {/* Full-page media */}
      <div className="absolute inset-0 bg-[#F9F7F4]">
        {!src ? (
          <div
            className="w-full h-full flex flex-col items-center justify-center gap-4 p-8 text-center"
            style={{ background: HERO_BACKGROUND }}
          >
            <div
              className="w-20 h-20 rounded-full bg-[#e8f0f7] border border-[#dbeafe] flex items-center justify-center shadow-[0_18px_44px_rgba(15,23,42,0.07)]"
            >
              <svg className="w-9 h-9 text-[#0f6abf]" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
            <p className="text-xl font-bold text-[#101828]">CEO message video</p>
            <p className="text-sm text-[#667085] max-w-md">
              Add your video URL to{" "}
              <code className="text-[#0f6abf] font-semibold">SOLUTION_VIDEO.src</code> in Solution.jsx
            </p>
          </div>
        ) : embedUrl ? (
          <iframe
            src={embedUrl}
            title={title}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <video
            className="w-full h-full object-cover"
            controls
            playsInline
            poster={poster || undefined}
            preload="metadata"
          >
            <source src={src} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )}
      </div>

      {/* Soft light fade into caption card */}
      <div
        className="absolute inset-x-0 bottom-0 h-[45%] pointer-events-none bg-gradient-to-t from-[#f8fbff] via-[#f8fbff]/70 to-transparent"
        aria-hidden="true"
      />

      <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-8 lg:p-10 pointer-events-none">
        <div className="max-w-[1100px] mx-auto">
          <div
            className="rounded-[28px] border border-[#e6edf7] bg-white/95 backdrop-blur-sm p-6 sm:p-8 shadow-[0_18px_45px_rgba(15,23,42,0.08)]"
          >
            <span
              className="inline-flex px-3.5 py-2 rounded-full bg-[#eef7ff] border border-[#dbeafe] text-[#24406f] text-[13px] font-extrabold"
            >
              Message from our CEO
            </span>
            <h2
              className="mt-4 text-[clamp(26px,3.5vw,42px)] leading-tight tracking-[-1.2px] font-bold text-[#101828] max-w-3xl"
            >
              {title}
            </h2>
            {speaker && (
              <p className="mt-2 text-base font-semibold text-[#0f6abf]">{speaker}</p>
            )}
            <p className="mt-3 max-w-2xl text-[17px] leading-relaxed text-[#667085]">
              {description}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const coreCards = [
  {
    num: "01",
    title: "Profile",
    description: "One living page for research, skills, projects, code and media.",
  },
  {
    num: "02",
    title: "Learning",
    description: "Turn expertise into courses, explainers and guided paths.",
  },
  {
    num: "03",
    title: "Community",
    description: "Discuss, mentor, collaborate and make ideas clearer.",
  },
  {
    num: "04",
    title: "Marketplace",
    description: "Connect ideas, prototypes and solutions with industry partners.",
  },
];

const valueCards = [
  { title: "Clarity", description: "Make complex ideas simple to understand." },
  { title: "Credibility", description: "Give real expertise a trusted space." },
  { title: "Collaboration", description: "Help people build knowledge together." },
  { title: "Impact", description: "Move research closer to the real world." },
];

const Badge = ({ children }) => (
  <span
    className="inline-flex px-3.5 py-2 rounded-full bg-[#eef7ff] border border-[#dbeafe] text-[#24406f] text-[13px] font-extrabold mb-4"
  >
    {children}
  </span>
);

const Solution = () => {
  return (
    <>
      <Navbar />
      <main className="bg-white text-[#101828]">
        {/* Hero */}
        <section
          className="min-h-[72vh] flex items-center px-5 py-[72px] sm:px-6"
          style={{ background: HERO_BACKGROUND }}
        >
          <div className="max-w-[1100px] mx-auto w-full">
            <Badge>Our Solution</Badge>
            <h1
              className="max-w-[860px] text-[clamp(42px,6vw,72px)] leading-none tracking-[-2.5px] font-bold"
            >
              AxionNest connects{" "}
              <span
                className="bg-gradient-to-br from-[#4f63f6] via-[#13b6d8] to-[#22b884] bg-clip-text text-transparent"
              >
                research, learning and industry.
              </span>
            </h1>
            <p className="max-w-[660px] mt-5 text-xl leading-relaxed text-[#667085]">
              One professional space to make academic knowledge visible, understandable
              and ready for collaboration.
            </p>
            <div
              className="mt-7 inline-flex px-5 py-3.5 rounded-2xl bg-white border border-[#e6edf7] shadow-[0_18px_44px_rgba(15,23,42,0.07)] font-black text-[#101828]"
            >
              Research × Learning × Community × Industry
            </div>
          </div>
        </section>

        {/* CEO full-page video */}
        <CeoVideoSection
          src={SOLUTION_VIDEO.src}
          poster={SOLUTION_VIDEO.poster}
          title={SOLUTION_VIDEO.title}
          speaker={SOLUTION_VIDEO.speaker}
          description={SOLUTION_VIDEO.description}
        />

        {/* What AxionNest does */}
        <section className="px-5 py-[72px] sm:px-6">
          <div className="max-w-[1100px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-5">
            <div
              className="p-9 rounded-[32px] bg-gradient-to-b from-white to-[#f8fbff] border border-[#e6edf7] shadow-[0_18px_45px_rgba(15,23,42,0.05)]"
            >
              <h2 className="text-[clamp(30px,4vw,46px)] leading-tight tracking-[-1.2px] font-bold">
                What AxionNest does
              </h2>
            </div>
            <div
              className="p-9 rounded-[32px] bg-white border border-[#e6edf7] shadow-[0_18px_45px_rgba(15,23,42,0.05)]"
            >
              <h3 className="text-xl font-bold">It turns scattered knowledge into one connected journey.</h3>
              <p className="mt-3.5 text-[17px] leading-relaxed text-[#667085]">
                Researchers can show their work, teach what they know, connect with peers
                and open doors to companies looking for innovation.
              </p>
            </div>
          </div>
        </section>

        {/* Core Solution */}
        <section className="px-5 py-[72px] sm:px-6 bg-[#f8fafc]">
          <div className="max-w-[1100px] mx-auto">
            <div className="max-w-[760px] mx-auto text-center">
              <Badge>Core Solution</Badge>
              <h2 className="text-[clamp(30px,4vw,46px)] leading-tight tracking-[-1.2px] font-bold">
                From knowledge to impact.
              </h2>
              <p className="mt-3.5 text-[17px] leading-relaxed text-[#667085]">
                AxionNest supports the full path from academic work to learning,
                collaboration and real-world use.
              </p>
            </div>
            <div className="mt-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {coreCards.map((card) => (
                <div
                  key={card.title}
                  className="p-6 rounded-[26px] bg-white border border-[#e6edf7]"
                >
                  <span className="block text-[#4f63f6] font-black mb-3">{card.num}</span>
                  <h3 className="text-xl font-bold">{card.title}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-[#667085]">{card.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="px-5 py-[72px] sm:px-6">
          <div className="max-w-[1100px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-5">
            <div
              className="p-9 rounded-[32px] bg-white border border-[#e6edf7] shadow-[0_18px_45px_rgba(15,23,42,0.05)]"
            >
              <Badge>Mission</Badge>
              <h2 className="text-[clamp(30px,4vw,46px)] leading-tight tracking-[-1.2px] font-bold">
                Make knowledge easier to discover, understand and apply.
              </h2>
              <p className="mt-3.5 text-[17px] leading-relaxed text-[#667085]">
                We help academic work move beyond documents into visibility,
                learning and collaboration.
              </p>
            </div>
            <div
              className="p-9 rounded-[32px] bg-white border border-[#e6edf7] shadow-[0_18px_45px_rgba(15,23,42,0.05)]"
            >
              <Badge>Vision</Badge>
              <h2 className="text-[clamp(30px,4vw,46px)] leading-tight tracking-[-1.2px] font-bold">
                Build a future where research moves faster into impact.
              </h2>
              <p className="mt-3.5 text-[17px] leading-relaxed text-[#667085]">
                AxionNest brings researchers, learners and companies closer together.
              </p>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="px-5 py-[72px] sm:px-6 bg-[#f8fafc]">
          <div className="max-w-[1100px] mx-auto">
            <div className="max-w-[760px] mx-auto text-center">
              <Badge>Values</Badge>
              <h2 className="text-[clamp(30px,4vw,46px)] leading-tight tracking-[-1.2px] font-bold">
                Clear. Credible. Collaborative. Impactful.
              </h2>
            </div>
            <div className="mt-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {valueCards.map((card) => (
                <div
                  key={card.title}
                  className="p-6 rounded-[26px] bg-white border border-[#e6edf7]"
                >
                  <h3 className="text-xl font-bold">{card.title}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-[#667085]">{card.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Founder Story */}
        <section className="px-5 py-[72px] sm:px-6">
          <div className="max-w-[1100px] mx-auto">
            <div
              className="p-8 sm:p-11 rounded-[36px] border border-[#e6edf7] shadow-[0_18px_45px_rgba(15,23,42,0.05)]"
              style={{
                background:
                  "radial-gradient(circle at top left, rgba(79,99,246,.12), transparent 34%), radial-gradient(circle at bottom right, rgba(34,184,132,.10), transparent 36%), #fff",
              }}
            >
              <Badge>Founder Story</Badge>
              <h2 className="text-[clamp(30px,4vw,46px)] leading-tight tracking-[-1.2px] font-bold">
                How AxionNest started
              </h2>
              <p className="mt-4 max-w-[850px] text-[17px] leading-relaxed text-[#667085]">
                During my research journey, I experienced two academic worlds:
                one where research struggled to reach industry, and another where
                university-company collaboration turned ideas into innovation.
              </p>
              <p className="mt-4 max-w-[850px] text-[17px] leading-relaxed text-[#667085]">
                I realized the issue was not a lack of knowledge.
                It was how knowledge moves.
              </p>
              <blockquote
                className="mt-6 pl-5 border-l-4 border-[#22b884] text-[#1d2939] text-[22px] leading-snug font-extrabold max-w-[850px]"
              >
                AxionNest was created to bring the journey of knowledge into one connected place.
              </blockquote>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-5 py-[72px] sm:px-6 text-center">
          <div className="max-w-[1100px] mx-auto">
            <div
              className="px-7 py-10 sm:py-11 rounded-[34px] border border-[#e6edf7] bg-gradient-to-br from-[#eef4ff] via-white to-[#effdf7]"
            >
              <h2 className="text-[clamp(30px,4vw,46px)] leading-tight tracking-[-1.2px] font-bold">
                Knowledge should move forward.
              </h2>
              <p className="max-w-[600px] mx-auto mt-3.5 text-[17px] leading-relaxed text-[#667085]">
                AxionNest helps it become visible, useful and connected.
              </p>
              <Link
                to="/register"
                className="inline-flex mt-6 px-6 py-4 rounded-[14px] font-black text-white bg-gradient-to-br from-[#4f63f6] to-[#22b884] hover:opacity-95 transition-opacity shadow-lg shadow-[#4f63f6]/20"
              >
                Get Started
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Solution;
